import { normalizeIndianPhone } from '@aawas/contracts';

/**
 * Turns pasted spreadsheet text into resident rows and tells the committee exactly which lines are wrong.
 * Line numbers are the spreadsheet's own (1-based, counting the header), so "line 187" can be found and fixed.
 */

export interface CsvRow {
  cells: string[];
  /** 1-based line in the pasted text. */
  line: number;
}

/** Small CSV/TSV parser: quotes, commas, tabs (paste from Excel/Sheets), CRLF. Blank lines are skipped. */
export function parseCsv(text: string): CsvRow[] {
  const rows: CsvRow[] = [];
  let cur: string[] = [];
  let field = '';
  let quoted = false;
  let line = 1;
  let rowLine = 1;
  const endRow = () => {
    cur.push(field.trim());
    field = '';
    if (cur.some(Boolean)) rows.push({ cells: cur, line: rowLine });
    cur = [];
  };
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (c === '"') quoted = false;
      else {
        if (c === '\n') line++;
        field += c;
      }
    } else if (c === '"') quoted = true;
    else if (c === ',' || c === '\t') {
      cur.push(field.trim());
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      endRow();
      line++;
      rowLine = line;
    } else field += c;
  }
  endRow();
  return rows;
}

export interface ImportRow {
  line: number;
  block: string;
  unit: string;
  name: string;
  phone: string;
  role: 'owner' | 'tenant';
}
export interface ImportProblem {
  line: number;
  /** What the row was, so it is recognisable. */
  what: string;
  why: string;
}

export function prepareImport(text: string): { rows: ImportRow[]; problems: ImportProblem[] } {
  const parsed = parseCsv(text);
  const hasHeader = /block|flat|tower/i.test(parsed[0]?.cells[0] ?? '');
  const rows: ImportRow[] = [];
  const problems: ImportProblem[] = [];
  const seen = new Map<string, number>();

  for (const [idx, r] of parsed.entries()) {
    if (idx === 0 && hasHeader) continue;
    const [block = '', unit = '', name = '', phone = '', roleRaw = ''] = r.cells;
    const what = [block && unit ? `${block}-${unit}` : block || unit, name].filter(Boolean).join(' ') || '(empty row)';
    const problem = (why: string) => problems.push({ line: r.line, what, why });

    if (r.cells.length < 4) problem('needs four columns: block, flat, name, mobile');
    else if (!block || !unit) problem('the block or flat number is missing');
    else if (!name) problem('the name is missing');
    else if (!normalizeIndianPhone(phone)) problem(`"${phone || 'blank'}" is not a valid 10-digit Indian mobile number`);
    else {
      const role = roleRaw.trim().toLowerCase();
      if (role && role !== 'owner' && role !== 'tenant') {
        problem(`role "${roleRaw}" must be owner or tenant (or leave it empty for owner)`);
        continue;
      }
      const key = `${block.toLowerCase()}|${unit.toLowerCase()}|${normalizeIndianPhone(phone)}`;
      const first = seen.get(key);
      if (first) {
        problem(`the same person and flat already appear on line ${first}`);
        continue;
      }
      seen.set(key, r.line);
      rows.push({ line: r.line, block, unit, name, phone, role: role === 'tenant' ? 'tenant' : 'owner' });
    }
  }
  return { rows, problems };
}
